<ion-content>
  <div class="support-chat-admin">
    <div class="chat-shell">

      <!-- Page title: desktop only. On mobile the panel headers carry the context and
           every pixel of height belongs to the conversation. -->
      <h1 class="hidden md:block text-2xl font-bold text-gray-900 mb-4 shrink-0">Support Chat Management</h1>

      <!-- Mobile is single-pane: the list and the conversation swap places.
           Desktop shows both side by side. -->
      <div class="flex gap-0 md:gap-6 flex-1 min-h-0">

        <!-- ─────────────────────────── Chat List Panel ─────────────────────────── -->
        <div
          class="w-full md:w-1/3 md:max-w-sm min-w-0 bg-white md:rounded-xl md:shadow-sm md:border md:border-gray-200 flex-col min-h-0"
          [ngClass]="selectedChat ? 'hidden md:flex' : 'flex'"
        >
          <!-- Search and Filter -->
          <div class="p-3 sm:p-4 border-b border-gray-200 shrink-0">
            <div class="flex items-center justify-between mb-3 md:hidden">
              <h1 class="text-lg font-bold text-gray-900">Support Chat</h1>
              <span *ngIf="totalChats" class="text-xs text-gray-500">{{ totalChats }} chats</span>
            </div>

            <div class="relative mb-3">
              <input
                type="text"
                [(ngModel)]="searchQuery"
                (keyup.enter)="onSearch()"
                placeholder="Search organization ID or message..."
                class="chat-input w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
              <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              <button
                *ngIf="searchQuery"
                (click)="clearSearch()"
                class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <i class="fa-solid fa-times"></i>
              </button>
            </div>

            <!-- Filter and Reload buttons -->
            <div class="flex items-center gap-2">
              <button
                (click)="loadChats()"
                [disabled]="isLoadingChats"
                class="tap-target flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50"
                title="Reload chat list"
                aria-label="Reload chat list"
              >
                <i class="fa-solid fa-rotate-right" [class.animate-spin]="isLoadingChats"></i>
              </button>
              <button
                (click)="openCreateChatModal()"
                class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary text-white hover:bg-primary-600 transition-colors min-h-[40px]"
                title="Create new chat"
              >
                <i class="fa-solid fa-plus"></i>
                <span>New Chat</span>
              </button>
            </div>
          </div>

          <!-- The list tabs. Each one is its own server-side list with its own page
               count — see `scopeFilter`. `flex-wrap` because five of them no longer
               fit a narrow sidebar on one line. -->
          <div class="flex flex-wrap items-center gap-1 px-3 pb-2 border-b border-gray-100">
            <button
              *ngFor="let option of tabs"
              (click)="setScopeFilter(option.id)"
              [title]="option.title"
              [attr.aria-label]="option.label || option.title"
              class="px-2.5 py-1 text-xs rounded-md transition-colors"
              [class.bg-primary]="scopeFilter === option.id"
              [class.text-white]="scopeFilter === option.id"
              [class.text-gray-600]="scopeFilter !== option.id"
              [class.hover:bg-gray-100]="scopeFilter !== option.id"
            >
              <i *ngIf="option.icon" class="fa-solid" [ngClass]="option.icon"></i>
              <span *ngIf="option.label">{{ option.label }}</span>
              <!-- Undecided credit claims. Red on every tab, including the selected one:
                   it is a count of people waiting, not a piece of tab chrome. -->
              <span
                *ngIf="option.id === 'credit-requests' && pendingCreditRequests"
                class="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
                [attr.aria-label]="pendingCreditRequests + ' credit requests waiting on a decision'"
              >{{ pendingCreditRequests }}</span>
            </button>
          </div>

          <!-- Chat List -->
          <div class="flex-1 overflow-y-auto min-h-0">
            <div *ngIf="isLoadingChats" class="flex items-center justify-center py-8">
              <ion-spinner name="crescent" class="text-primary"></ion-spinner>
            </div>

            <div *ngIf="!isLoadingChats && chats.length === 0" class="flex flex-col items-center justify-center py-12 text-gray-500">
              <i class="fa-solid fa-inbox text-4xl mb-2 opacity-40"></i>
              <span class="text-sm">No chats found</span>
            </div>

            <div
              *ngFor="let chat of chats"
              (click)="selectChat(chat)"
              class="chat-item p-3 sm:p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors relative"
              [class.bg-primary-50]="selectedChat?._id === chat._id"
              [class.border-l-4]="selectedChat?._id === chat._id || needsReply(chat)"
              [class.border-l-primary]="selectedChat?._id === chat._id && !needsReply(chat)"
              [class.border-l-red-500]="needsReply(chat)"
              [class.bg-red-50]="needsReply(chat) && selectedChat?._id !== chat._id"
            >
              <span *ngIf="needsReply(chat)" class="absolute top-3 right-3 h-2.5 w-2.5 bg-red-500 rounded-full animate-pulse"></span>

              <div class="flex items-center justify-between gap-2 mb-1 pr-4">
                <span class="flex items-center gap-1.5 min-w-0">
                  <!-- PHASE 19: an ACCOUNT chat has no project, so it is badged and
                       labelled by the person instead of showing an empty name. -->
                  <span
                    *ngIf="isAccountChat(chat)"
                    class="px-1.5 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide shrink-0 bg-indigo-100 text-indigo-700"
                    title="Account-scoped chat (Totalum Platform)"
                  >Account</span>
                  <span class="font-semibold text-sm truncate" [class.text-gray-900]="!needsReply(chat)" [class.text-red-600]="needsReply(chat)">{{ chatLabel(chat) }}</span>
                </span>
                <span class="text-xs text-gray-500 shrink-0">{{ getLastMessageTime(chat) }}</span>
              </div>

              <div class="flex items-center gap-2 mb-1">
                <span *ngIf="chat.userName || chat.userEmail" class="text-xs text-gray-600 truncate max-w-[60%]">
                  {{ chat.userName || chat.userEmail }}
                </span>
                <span
                  *ngIf="chat.userPlan"
                  class="px-1.5 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide shrink-0"
                  [class.bg-gray-200]="chat.userPlan === 'free'"
                  [class.text-gray-500]="chat.userPlan === 'free'"
                  [class.bg-blue-600]="chat.userPlan === 'starter' || chat.userPlan === 'shared'"
                  [class.text-white]="chat.userPlan !== 'free'"
                  [class.bg-emerald-600]="isProfesionalPlan(chat.userPlan)"
                  [class.bg-gradient-to-r]="chat.userPlan === 'enterprise'"
                  [class.from-indigo-600]="chat.userPlan === 'enterprise'"
                  [class.to-purple-600]="chat.userPlan === 'enterprise'"
                  [class.bg-amber-500]="chat.userPlan === 'business'"
                >{{ chat.userPlan }}</span>
              </div>

              <p class="text-sm text-gray-500 truncate">{{ getLastMessage(chat) }}</p>
              <div class="flex items-center mt-1">
                <span class="text-xs text-gray-400">{{ chat.messages?.length || 0 }} messages</span>
                <span *ngIf="needsReply(chat)" class="ml-2 text-xs text-red-500 font-medium">• Needs response</span>
                <!-- ⭐ Pin / unpin. `stopPropagation` in the handler is what stops the tap
                     from also opening the chat — the whole row is clickable. It sits on
                     this line rather than the top-right corner, which the unread dot owns. -->
                <button
                  type="button"
                  (click)="toggleFavourite(chat, $event)"
                  class="ml-auto -my-1 w-7 h-7 grid place-items-center rounded-md transition-colors shrink-0"
                  [ngClass]="chat.favourite ? 'text-amber-400 hover:bg-amber-50' : 'text-gray-300 hover:text-amber-400 hover:bg-gray-100'"
                  [title]="chat.favourite ? 'Remove from favourites' : 'Add to favourites'"
                  [attr.aria-label]="chat.favourite ? 'Remove from favourites' : 'Add to favourites'"
                  [attr.aria-pressed]="!!chat.favourite"
                >
                  <i class="fa-star text-xs" [ngClass]="chat.favourite ? 'fa-solid' : 'fa-regular'"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div *ngIf="totalPages > 1" class="p-3 border-t border-gray-200 flex items-center justify-between shrink-0">
            <button
              (click)="previousPage()"
              [disabled]="currentPage === 1"
              class="tap-target border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="text-sm text-gray-600">Page {{ currentPage }} of {{ totalPages }}</span>
            <button
              (click)="nextPage()"
              [disabled]="currentPage === totalPages"
              class="tap-target border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- ─────────────────────────── Chat Panel ─────────────────────────── -->
        <!-- min-w-0 is load-bearing: a flex item defaults to min-width:auto, so without it
             this panel is sized by its widest child and overflows narrow screens. -->
        <div
          class="flex-1 min-w-0 bg-white md:rounded-xl md:shadow-sm md:border md:border-gray-200 flex-col min-h-0"
          [ngClass]="selectedChat ? 'flex' : 'hidden md:flex'"
        >
          <!-- No Chat Selected (desktop only — on mobile the list occupies this state) -->
          <div *ngIf="!selectedChat" class="flex-1 flex flex-col items-center justify-center text-gray-500">
            <i class="fa-solid fa-comments text-6xl mb-4 opacity-40"></i>
            <span class="text-lg">Select a chat to view messages</span>
          </div>

          <ng-container *ngIf="selectedChat">
            <!-- Chat Header -->
            <div class="px-3 py-2.5 sm:p-4 border-b border-gray-200 bg-gray-50 shrink-0">
              <div class="flex items-center gap-2 sm:gap-3">

                <!-- Back to list (mobile only) -->
                <button
                  (click)="backToList()"
                  class="tap-target md:hidden shrink-0 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors"
                  aria-label="Back to chat list"
                >
                  <i class="fa-solid fa-arrow-left"></i>
                </button>

                <!-- Identity -->
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 min-w-0">
                    <a
                      *ngIf="!isAccountChat(selectedChat)"
                      [href]="'https://web.totalum.app/table/totalum_instances?organizationid=' + selectedChat.organizationId"
                      target="_blank"
                      rel="noopener"
                      class="font-semibold text-gray-900 hover:text-primary hover:underline cursor-pointer truncate text-sm sm:text-base"
                    >{{ selectedChat.organizationId }}</a>
                    <!-- PHASE 19: an account chat has no project page to link to,
                         so it shows the person's name with an "Account" badge. -->
                    <span
                      *ngIf="isAccountChat(selectedChat)"
                      class="flex items-center gap-2 min-w-0"
                    >
                      <span
                        class="px-1.5 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide shrink-0 bg-indigo-100 text-indigo-700"
                      >Account</span>
                      <!-- ⭐ THE NAME OPENS THE CUSTOMER PANEL — the same slide-over the
                           CEO users page opens on a row. Inert (and styled as plain text)
                           when the chat carries no account id to open. -->
                      <span
                        class="font-semibold text-gray-900 truncate text-sm sm:text-base"
                        [ngClass]="detailUserIdOf(selectedChat) ? 'cursor-pointer hover:text-primary hover:underline' : ''"
                        [attr.role]="detailUserIdOf(selectedChat) ? 'button' : null"
                        [attr.tabindex]="detailUserIdOf(selectedChat) ? 0 : null"
                        [attr.title]="detailUserIdOf(selectedChat) ? 'Open this customer\'s account' : null"
                        (click)="openUserDetail(selectedChat)"
                        (keydown.enter)="openUserDetail(selectedChat)"
                      >{{ chatLabel(selectedChat) }}</span>
                    </span>
                    <span
                      *ngIf="selectedChat.userPlan"
                      class="px-2 py-0.5 text-[10px] sm:text-xs font-bold rounded uppercase tracking-wide shrink-0"
                      [class.bg-gray-200]="selectedChat.userPlan === 'free'"
                      [class.text-gray-500]="selectedChat.userPlan === 'free'"
                      [class.bg-blue-600]="selectedChat.userPlan === 'starter' || selectedChat.userPlan === 'shared'"
                      [class.text-white]="selectedChat.userPlan !== 'free'"
                      [class.bg-emerald-600]="isProfesionalPlan(selectedChat.userPlan)"
                      [class.bg-gradient-to-r]="selectedChat.userPlan === 'enterprise'"
                      [class.from-indigo-600]="selectedChat.userPlan === 'enterprise'"
                      [class.to-purple-600]="selectedChat.userPlan === 'enterprise'"
                      [class.bg-amber-500]="selectedChat.userPlan === 'business'"
                    >{{ selectedChat.userPlan }}</span>
                    <span *ngIf="needsReply(selectedChat)" class="hidden sm:inline px-2 py-0.5 text-xs font-medium rounded bg-red-500 text-white shrink-0">
                      Needs response
                    </span>
                  </div>
                  <div class="flex items-center gap-1.5 text-xs sm:text-sm text-gray-500 min-w-0">
                    <span
                      *ngIf="selectedChat.userName"
                      class="truncate"
                      [ngClass]="detailUserIdOf(selectedChat) ? 'cursor-pointer text-primary hover:underline' : ''"
                      [attr.role]="detailUserIdOf(selectedChat) ? 'button' : null"
                      [attr.tabindex]="detailUserIdOf(selectedChat) ? 0 : null"
                      [attr.title]="detailUserIdOf(selectedChat) ? 'Open this customer\'s account' : null"
                      (click)="openUserDetail(selectedChat)"
                      (keydown.enter)="openUserDetail(selectedChat)"
                    >{{ selectedChat.userName }}</span>
                    <span *ngIf="selectedChat.userName && selectedChat.userEmail" class="shrink-0">•</span>
                    <span *ngIf="selectedChat.userEmail" class="truncate">{{ selectedChat.userEmail }}</span>
                    <span *ngIf="!selectedChat.userName && !selectedChat.userEmail">No user info</span>
                  </div>
                </div>

                <!-- Actions -->
                <div class="flex items-center gap-1.5 shrink-0">
                  <!-- ⭐ The same pin, reachable on mobile where the list is off screen. -->
                  <button
                    type="button"
                    (click)="toggleFavourite(selectedChat)"
                    class="tap-target rounded-lg transition-colors"
                    [ngClass]="selectedChat.favourite ? 'text-amber-400 hover:bg-amber-50' : 'text-gray-400 hover:text-amber-400 hover:bg-gray-200'"
                    [title]="selectedChat.favourite ? 'Remove from favourites' : 'Add to favourites'"
                    [attr.aria-label]="selectedChat.favourite ? 'Remove from favourites' : 'Add to favourites'"
                    [attr.aria-pressed]="!!selectedChat.favourite"
                  >
                    <i class="fa-star" [ngClass]="selectedChat.favourite ? 'fa-solid' : 'fa-regular'"></i>
                  </button>
                  <!-- Credits: the badge and the +  read as one control -->
                  <div class="flex items-center rounded-lg bg-yellow-100 overflow-hidden">
                    <span class="pl-2 pr-1.5 py-1 text-sm font-medium text-yellow-800 flex items-center gap-1">
                      <i class="fa-solid fa-coins text-yellow-600 text-xs"></i>
                      <span *ngIf="isLoadingCredits">…</span>
                      <span *ngIf="!isLoadingCredits">{{ userCredits !== null ? userCredits : '?' }}</span>
                    </span>
                    <button
                      (click)="openGiveCreditsModal()"
                      class="w-8 h-8 flex items-center justify-center text-sm font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                      title="Give credits"
                      aria-label="Give credits"
                    ><i class="fa-solid fa-plus text-xs"></i></button>
                  </div>

                  <!-- Mark as responded: icon-only on mobile, labelled on desktop -->
                  <button
                    *ngIf="needsReply(selectedChat)"
                    (click)="markAsResponded()"
                    [disabled]="isMarkingResponded"
                    class="min-h-[32px] px-2 sm:px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                    title="Mark as responded without sending a message"
                  >
                    <ion-spinner *ngIf="isMarkingResponded" name="crescent" class="w-4 h-4"></ion-spinner>
                    <i *ngIf="!isMarkingResponded" class="fa-solid fa-check"></i>
                    <span class="hidden sm:inline">Mark responded</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Messages -->
            <div class="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 min-h-0" #messagesContainer>
              <div *ngIf="isLoadingChat" class="flex items-center justify-center py-8">
                <ion-spinner name="crescent" class="text-primary"></ion-spinner>
              </div>

              <ng-container *ngIf="!isLoadingChat">
                <ng-container *ngFor="let message of selectedChat.messages; let i = index">
                  <!-- Day Separator -->
                  <div *ngIf="shouldShowDaySeparator(i)" class="flex items-center justify-center my-4">
                    <div class="flex-1 border-t border-gray-200"></div>
                    <span class="px-3 py-1 text-xs text-gray-500 bg-gray-100 rounded-full font-medium shrink-0">
                      {{ getMessageDay(i) }}
                    </span>
                    <div class="flex-1 border-t border-gray-200"></div>
                  </div>

                  <div
                    class="flex group items-center"
                    [class.justify-end]="message.sender === 'assistant'"
                    [class.justify-start]="message.sender === 'user'"
                  >
                    <!-- Edit/Delete (left of assistant messages).
                         .msg-actions is always visible on touch and hover-revealed on pointer
                         devices — see the (hover: hover) rule in the scss. -->
                    <div
                      *ngIf="message.sender === 'assistant' && editingMessageIndex !== i"
                      class="msg-actions items-center gap-0.5 mr-1"
                    >
                      <button
                        (click)="startEditMessage(i, message)"
                        class="tap-target text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit message"
                        aria-label="Edit message"
                      >
                        <i class="fa-solid fa-pen text-xs"></i>
                      </button>
                      <button
                        (click)="deleteMessage(i)"
                        [disabled]="isDeleting"
                        class="tap-target text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete message"
                        aria-label="Delete message"
                      >
                        <i class="fa-solid fa-trash text-xs"></i>
                      </button>
                    </div>

                    <div
                      class="max-w-[82%] sm:max-w-[70%] rounded-2xl px-3 py-2 sm:p-3 relative"
                      [class.bg-primary]="message.sender === 'assistant'"
                      [class.text-white]="message.sender === 'assistant'"
                      [class.rounded-br-sm]="message.sender === 'assistant'"
                      [class.bg-gray-100]="message.sender === 'user'"
                      [class.text-gray-900]="message.sender === 'user'"
                      [class.rounded-bl-sm]="message.sender === 'user'"
                    >
                      <!-- File Attachment -->
                      <div *ngIf="message.fileUrl" class="mb-2">
                        <div *ngIf="isImageFile(message.fileType)" class="cursor-pointer" (click)="downloadFile(message.fileUrl, message.fileName)">
                          <img [src]="message.fileUrl" [alt]="message.fileName" class="max-w-full sm:max-w-[200px] rounded">
                        </div>
                        <div
                          *ngIf="!isImageFile(message.fileType)"
                          class="flex items-center gap-2 p-2 rounded cursor-pointer"
                          [class.bg-white]="message.sender === 'assistant'"
                          [class.bg-opacity-20]="message.sender === 'assistant'"
                          [class.bg-gray-200]="message.sender === 'user'"
                          (click)="downloadFile(message.fileUrl, message.fileName)"
                        >
                          <ion-icon [name]="getFileIcon(message.fileType)"></ion-icon>
                          <span class="text-sm truncate">{{ message.fileName }}</span>
                          <ion-icon name="download-outline"></ion-icon>
                        </div>
                      </div>

                      <!-- Edit Mode -->
                      <ng-container *ngIf="editingMessageIndex === i">
                        <textarea
                          [(ngModel)]="editingMessageContent"
                          (keydown)="onEditKeyDown($event)"
                          class="chat-input w-full px-2 py-1 border border-white/30 rounded bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
                          rows="3"
                          autofocus
                        ></textarea>
                        <div class="flex justify-end gap-2 mt-2">
                          <button
                            (click)="cancelEdit()"
                            class="px-3 py-1.5 text-xs bg-white/20 rounded hover:bg-white/30 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            (click)="saveEdit()"
                            [disabled]="isEditing || !editingMessageContent.trim()"
                            class="px-3 py-1.5 text-xs bg-white rounded text-primary hover:bg-white/90 transition-colors disabled:opacity-50"
                          >
                            <ion-spinner *ngIf="isEditing" name="crescent" class="w-3 h-3"></ion-spinner>
                            <span *ngIf="!isEditing">Save</span>
                          </button>
                        </div>
                      </ng-container>

                      <!-- ═══ ⭐ MESSAGE TEXT (normal mode) ═════════════════════
                           `innerHTML` + `linkify` so a URL a customer pasted is a
                           real link instead of text to select and retype.

                           ⚠️ THE PIPE ESCAPES THE BODY ITSELF — see `linkify.pipe.ts`.
                           This is customer-written text, so nothing here may be
                           interpolated raw; if the pipe is ever removed from this
                           binding the `innerHTML` must go with it. -->
                      <p
                        *ngIf="editingMessageIndex !== i && message.message && message.message !== 'Attached file'"
                        class="text-sm whitespace-pre-wrap break-words"
                        [innerHTML]="message.message | linkify"
                      ></p>

                      <!-- ═══ ⭐ TRANSLATE THIS MESSAGE ═════════════════════════
                           Offered only on CUSTOMER messages the backend judged to be
                           in neither Spanish nor English. One click fetches and shows
                           the English; a second collapses it.

                           ⚠️ A BUTTON, NOT A CLICK ON THE BUBBLE. The bubble already
                           carries selectable text — making the whole thing a click
                           target means every attempt to select a phrase fires a
                           request. -->
                      <ng-container *ngIf="editingMessageIndex !== i && canTranslate(message)">
                        <button
                          type="button"
                          (click)="toggleTranslation(message)"
                          [disabled]="isTranslating(message)"
                          class="mt-1.5 inline-flex items-center gap-1.5 rounded text-[11px] font-medium text-blue-700 hover:text-blue-900 hover:underline disabled:opacity-60"
                          [attr.aria-expanded]="isTranslationOpen(message)"
                        >
                          <ion-spinner
                            *ngIf="isTranslating(message)"
                            name="crescent"
                            class="h-3 w-3"
                          ></ion-spinner>
                          <i *ngIf="!isTranslating(message)" class="fa-solid fa-language"></i>
                          <span *ngIf="isTranslating(message)">Translating…</span>
                          <span *ngIf="!isTranslating(message)">
                            {{ isTranslationOpen(message) ? 'Hide translation' : 'Translate' }}
                          </span>
                        </button>

                        <!-- The translation itself. Visually separated from the
                             original so it can never be mistaken for what the
                             customer actually wrote. -->
                        <div
                          *ngIf="isTranslationOpen(message) && translationOf(message) as translation"
                          class="mt-1.5 border-l-2 border-blue-300 pl-2"
                        >
                          <p class="text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                            English
                            <span *ngIf="translation.sourceLanguage" class="font-normal normal-case opacity-80">
                              · from {{ translation.sourceLanguage }}
                            </span>
                          </p>
                          <!-- Same body, translated — links must stay clickable here too. -->
                          <p class="text-sm whitespace-pre-wrap break-words"
                             [innerHTML]="translation.englishText | linkify"></p>
                        </div>

                        <p *ngIf="translationErrorFor(message)" class="mt-1 text-[11px] text-red-600">
                          {{ translationErrorFor(message) }}
                        </p>
                      </ng-container>

                      <p class="text-[11px] mt-1 opacity-70">
                        {{ formatTime(message.createdAt) }}
                        <span *ngIf="message.editedAt" class="ml-1 italic">(edited)</span>
                      </p>
                    </div>

                    <!-- Edit/Delete (right of user messages) -->
                    <div
                      *ngIf="message.sender === 'user' && editingMessageIndex !== i"
                      class="msg-actions items-center gap-0.5 ml-1"
                    >
                      <button
                        (click)="startEditMessage(i, message)"
                        class="tap-target text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit message"
                        aria-label="Edit message"
                      >
                        <i class="fa-solid fa-pen text-xs"></i>
                      </button>
                      <button
                        (click)="deleteMessage(i)"
                        [disabled]="isDeleting"
                        class="tap-target text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete message"
                        aria-label="Delete message"
                      >
                        <i class="fa-solid fa-trash text-xs"></i>
                      </button>
                    </div>
                  </div>
                </ng-container>
              </ng-container>
            </div>

            <!-- Composer -->
            <div class="composer p-2.5 sm:p-4 border-t border-gray-200 shrink-0">
              <!-- File URL Input (collapsible) -->
              <div *ngIf="showFileUrlInput" class="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-medium text-gray-700">Attach file by URL</span>
                  <button (click)="clearFileAttachment()" class="tap-target text-gray-400 hover:text-gray-600" aria-label="Close attachment panel">
                    <i class="fa-solid fa-times"></i>
                  </button>
                </div>
                <!-- ⚠️ `ngModelChange` CLEARS THE SNIFFED TYPE. It belongs to the
                     uploaded file, and typing a different URL over it would send
                     the previous file's type with this one's link. -->
                <input
                  type="text"
                  inputmode="url"
                  [(ngModel)]="attachedFileUrl"
                  (ngModelChange)="attachedFileType = ''"
                  placeholder="https://example.com/file.pdf"
                  class="chat-input w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent mb-2"
                >
                <input
                  type="text"
                  [(ngModel)]="attachedFileName"
                  placeholder="File name (optional)"
                  class="chat-input w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
              </div>

              <!-- ═══ THE AI DRAFT REPLY ═══════════════════════════════════════
                   Written when the CUSTOMER sent their message, from totalum.app/docs,
                   in their language. It is a draft: "Use this" fills the box below and
                   nothing here can send it. -->
              <!-- ═══ ⭐⭐ A CREDIT REQUEST WAITING ON A DECISION ═══════════════
                   The platform's automatic acknowledgement is the last message, so
                   nobody has actually answered yet — see `isAwaitingClaimDecision`.
                   These three replace the AI draft, which cannot know whether a post
                   is real and so is always wrong on this one message. They fill the
                   box; giving the credits is what approves the request. -->
              <div *ngIf="isClaimPending" class="mb-3 rounded-xl border border-amber-200 bg-amber-50/70 p-2.5">
                <div class="flex items-start gap-2">
                  <i class="fa-solid fa-gift mt-0.5 shrink-0 text-amber-500"></i>
                  <div class="min-w-0 flex-1">
                    <p class="text-[11px] font-semibold text-amber-900">Credit request — waiting on your decision</p>
                    <p class="mt-0.5 text-[11px] leading-snug text-amber-700">
                      Giving the credits is what approves it. These only fill the box below.
                    </p>
                    <div class="mt-2 flex flex-wrap gap-1.5">
                      <button
                        *ngFor="let reply of claimReplies"
                        type="button"
                        (click)="useClaimReply(reply.text)"
                        [title]="reply.text"
                        class="rounded-lg border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
                      >{{ reply.label }}</button>
                    </div>
                  </div>
                </div>
              </div>

              <div
                *ngIf="suggestion && !suggestionDismissed && !isClaimPending"
                class="mb-3 rounded-xl border border-indigo-200 bg-indigo-50/70 p-3"
              >
                <div class="flex items-start gap-2">
                  <i class="fa-solid fa-wand-magic-sparkles mt-0.5 shrink-0 text-indigo-500"></i>
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span class="text-xs font-semibold text-indigo-900">Suggested reply</span>
                      <span class="rounded-full bg-indigo-100 px-1.5 py-px text-[10px] font-medium uppercase text-indigo-700">
                        {{ suggestion.language }}
                      </span>
                      <!-- The customer has written again since this was drafted. -->
                      <span
                        *ngIf="isSuggestionStale"
                        class="rounded-full bg-amber-100 px-1.5 py-px text-[10px] font-medium text-amber-800"
                        title="The customer has sent another message since this draft was written"
                      >
                        out of date
                      </span>
                      <!-- ⚠️ HIDE, NOT DISCARD. This one is temporary and says so:
                           the card collapses and the "Show suggested reply" link
                           below brings it back. Throwing the draft away for good is
                           the labelled button at the bottom. -->
                      <button
                        (click)="suggestionDismissed = true"
                        class="ml-auto shrink-0 text-xs text-indigo-400 hover:text-indigo-700"
                        aria-label="Hide the suggested reply for now"
                        title="Hide it for now"
                      >
                        <i class="fa-solid fa-times"></i>
                      </button>
                    </div>

                    <p class="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-gray-800">{{ suggestion.text }}</p>

                    <!-- ⚠️ ONLY WHEN THE CUSTOMER WROTE IN NEITHER ENGLISH NOR SPANISH.
                         It exists so the agent can read what they are about to send. -->
                    <div *ngIf="suggestion.englishText" class="mt-2">
                      <button
                        (click)="showSuggestionEnglish = !showSuggestionEnglish"
                        class="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        <i class="fa-solid" [class.fa-chevron-right]="!showSuggestionEnglish" [class.fa-chevron-down]="showSuggestionEnglish"></i>
                        {{ showSuggestionEnglish ? 'Hide' : 'Show' }} English translation
                      </button>
                      <p
                        *ngIf="showSuggestionEnglish"
                        class="mt-1 whitespace-pre-wrap rounded-lg bg-white/70 p-2 text-sm italic leading-relaxed text-gray-600"
                      >{{ suggestion.englishText }}</p>
                    </div>

                    <!-- The docs pages it is based on, so the agent can check it. -->
                    <div *ngIf="suggestion.sources?.length" class="mt-2 flex flex-wrap gap-1.5">
                      <a
                        *ngFor="let source of suggestion.sources"
                        [href]="source.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="max-w-full truncate rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-50"
                      >
                        <i class="fa-solid fa-book mr-1"></i>{{ source.title }}
                      </a>
                    </div>

                    <div class="mt-2.5 flex items-center gap-2">
                      <button
                        (click)="useSuggestion()"
                        class="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                      >
                        Use this
                      </button>
                      <!-- ⭐ PERMANENT. Unlike the × above, this tells the server the
                           draft was rejected, so it is never offered again — on this
                           machine or any other. -->
                      <button
                        (click)="discardSuggestion()"
                        class="rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                        title="Throw this draft away — it won't come back"
                      >
                        Discard
                      </button>
                      <span class="text-[11px] text-indigo-400">
                        {{ suggestion.model }}
                        <span *ngIf="suggestion.toolCallsMade !== undefined">
                          · {{ suggestion.toolCallsMade }} {{ suggestion.toolCallsMade === 1 ? 'lookup' : 'lookups' }}
                        </span>
                        <span *ngIf="suggestionUsage"> · {{ suggestionUsage.used }}/{{ suggestionUsage.max }} today</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- A dismissed or used draft stays one click away. -->
              <button
                *ngIf="suggestion && suggestionDismissed && !isClaimPending"
                (click)="suggestionDismissed = false"
                class="mb-2 text-xs font-medium text-indigo-500 hover:text-indigo-700"
              >
                <i class="fa-solid fa-wand-magic-sparkles mr-1"></i>Show suggested reply
              </button>

              <!-- Attached file indicator -->
              <div *ngIf="attachedFileUrl && !showFileUrlInput" class="mb-2 flex items-center gap-2 text-sm text-blue-600">
                <i class="fa-solid fa-paperclip shrink-0"></i>
                <span class="truncate flex-1">{{ attachedFileName || getFileNameFromUrl(attachedFileUrl) }}</span>
                <button (click)="clearFileAttachment()" class="tap-target text-gray-400 hover:text-red-500 shrink-0" aria-label="Remove attachment">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>

              <!-- Upload failure. Beside the composer, not a toast: the agent is
                   about to pick another file and needs the reason to stay put. -->
              <div *ngIf="uploadError" class="mb-2 flex items-start gap-2 text-sm text-red-600">
                <i class="fa-solid fa-circle-exclamation mt-0.5 shrink-0"></i>
                <span class="flex-1">{{ uploadError }}</span>
                <button (click)="uploadError = null" class="tap-target text-gray-400 hover:text-gray-600 shrink-0" aria-label="Dismiss">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>

              <div class="flex items-end gap-2">
                <!-- ⭐ UPLOAD A FILE.
                     The input is hidden and the button is the affordance, because a
                     native file input cannot be styled to match this composer.
                     `accept` filters the picker as a convenience; the SERVER decides
                     from the file's bytes and is the only real check. -->
                <input
                  #fileInput
                  type="file"
                  class="hidden"
                  [accept]="uploadAccept"
                  (change)="onFileSelected($event)"
                />
                <button
                  (click)="openFilePicker()"
                  [disabled]="isUploadingFile || isSending"
                  class="tap-target shrink-0 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  title="Upload a file"
                  aria-label="Upload a file"
                >
                  <ion-spinner *ngIf="isUploadingFile" name="crescent" class="h-4 w-4"></ion-spinner>
                  <i *ngIf="!isUploadingFile" class="fa-solid fa-arrow-up-from-bracket" [class.text-blue-600]="attachedFileUrl"></i>
                </button>
                <button
                  (click)="toggleFileUrlInput()"
                  class="tap-target shrink-0 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  [class.bg-blue-50]="showFileUrlInput"
                  [class.border-blue-300]="showFileUrlInput"
                  title="Attach file by URL"
                  aria-label="Attach file by URL"
                >
                  <i class="fa-solid fa-paperclip" [class.text-blue-600]="showFileUrlInput || attachedFileUrl"></i>
                </button>
                <textarea
                  #messageTextarea
                  [(ngModel)]="currentMessage"
                  (ngModelChange)="autoResizeTextarea()"
                  (keydown)="onKeyDown($event)"
                  placeholder="Type your response…"
                  class="chat-input flex-1 min-w-0 px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows="1"
                  [disabled]="isSending"
                ></textarea>
                <!-- ⭐ POLISH THE DRAFT.
                     ⚠️ IT DOES NOT TOUCH THE TEXTAREA. The correction opens in a modal
                     and only reaches the composer if the admin presses Apply — see
                     `polishMessage()`. Indigo + the wand icon is the established
                     visual language for the LLM features on this page. -->
                <button
                  (click)="polishMessage()"
                  [disabled]="!currentMessage.trim() || isPolishing || isSending"
                  class="tap-target shrink-0 border border-gray-300 rounded-lg hover:bg-indigo-50 hover:border-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Polish — fix the grammar of this draft"
                  aria-label="Polish the draft"
                >
                  <ion-spinner *ngIf="isPolishing" name="crescent" class="h-4 w-4"></ion-spinner>
                  <i *ngIf="!isPolishing" class="fa-solid fa-wand-magic-sparkles text-indigo-600"></i>
                </button>
                <button
                  (click)="sendMessage()"
                  [disabled]="(!currentMessage.trim() && !attachedFileUrl) || isSending"
                  class="tap-target shrink-0 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:px-6"
                  aria-label="Send message"
                >
                  <ion-spinner *ngIf="isSending" name="crescent" class="w-5 h-5"></ion-spinner>
                  <i *ngIf="!isSending" class="fa-solid fa-paper-plane"></i>
                </button>
              </div>
              <p class="hidden sm:block mt-1.5 text-[11px] text-gray-400">Enter to send · Shift+Enter for a new line</p>
            </div>
          </ng-container>
        </div>
      </div>
    </div>

    <!-- ─────────────────────────── Give Credits Selection Modal ─────────────────────────── -->
    <div *ngIf="showGiveCreditsModal" class="modal-backdrop" (click)="closeGiveCreditsModal()">
      <div class="modal-sheet max-w-sm" (click)="$event.stopPropagation()">
        <div class="p-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h3 class="text-lg font-semibold text-gray-900">Give Credits</h3>
          <button (click)="closeGiveCreditsModal()" class="tap-target text-gray-400 hover:text-gray-600" aria-label="Close">
            <i class="fa-solid fa-times text-lg"></i>
          </button>
        </div>
        <div class="p-4 sm:p-6 overflow-y-auto">
          <p class="text-sm text-gray-600 mb-4">
            Select amount to give to <strong>{{ selectedChat?.organizationId }}</strong>
          </p>
          <div class="grid grid-cols-2 sm:grid-cols-1 gap-2">
            <button
              *ngFor="let amount of creditAmounts"
              (click)="openGiveCreditsConfirm(amount); showGiveCreditsModal = false"
              class="w-full px-4 py-3 text-sm font-semibold rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors flex items-center justify-between"
            >
              <span>+{{ amount }} credits</span>
              <i class="fa-solid fa-chevron-right text-xs text-emerald-400"></i>
            </button>
          </div>

          <!-- Custom amount -->
          <div class="mt-4 pt-4 border-t border-gray-200">
            <label class="block text-sm text-gray-600 mb-2">Or enter a custom amount</label>
            <div class="flex gap-2">
              <input
                type="number"
                inputmode="numeric"
                [ngModel]="customCredits"
                (ngModelChange)="onCustomCreditsChange($event)"
                min="1"
                max="500000"
                step="1"
                placeholder="1 – 500000"
                class="chat-input flex-1 min-w-0 px-3 py-2 text-gray-900 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <button
                (click)="openGiveCustomCreditsConfirm()"
                [disabled]="!isCustomCreditsValid"
                class="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                <span>Continue</span>
                <i class="fa-solid fa-chevron-right text-xs"></i>
              </button>
            </div>
            <p *ngIf="customCredits !== null && !isCustomCreditsValid" class="text-xs text-red-500 mt-1">
              Enter a whole number between 1 and 500000.
            </p>
          </div>

          <!-- Remove credits (subtle, hidden by default) -->
          <div class="mt-4 pt-3 border-t border-gray-100">
            <button
              *ngIf="!showRemoveCreditsSection"
              (click)="showRemoveCreditsSection = true"
              class="text-xs text-gray-400 hover:text-red-500 transition-colors py-1"
            >Remove credits instead</button>

            <div *ngIf="showRemoveCreditsSection">
              <label class="block text-sm text-red-600 mb-2">Remove a custom amount</label>
              <div class="flex gap-2">
                <input
                  type="number"
                  inputmode="numeric"
                  [ngModel]="customRemoveCredits"
                  (ngModelChange)="onCustomRemoveCreditsChange($event)"
                  min="1"
                  max="500000"
                  step="1"
                  placeholder="1 – 500000"
                  class="chat-input flex-1 min-w-0 px-3 py-2 text-gray-900 border border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <button
                  (click)="openRemoveCreditsConfirm()"
                  [disabled]="!isCustomRemoveCreditsValid"
                  class="px-4 py-2 text-sm font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  <span>Remove</span>
                  <i class="fa-solid fa-chevron-right text-xs"></i>
                </button>
              </div>
              <p *ngIf="customRemoveCredits !== null && !isCustomRemoveCreditsValid" class="text-xs text-red-500 mt-1">
                Enter a whole number between 1 and 500000.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─────────────────────────── Give Credits Confirmation Modal ─────────────────────────── -->
    <div *ngIf="showGiveCreditsConfirm" class="modal-backdrop" (click)="cancelGiveCredits()">
      <div class="modal-sheet max-w-sm" (click)="$event.stopPropagation()">
        <div class="p-6 text-center">
          <div
            class="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
            [ngClass]="creditsMode === 'remove' ? 'bg-red-100' : 'bg-emerald-100'"
          >
            <i
              class="text-xl"
              [ngClass]="creditsMode === 'remove' ? 'fa-solid fa-minus text-red-600' : 'fa-solid fa-coins text-emerald-600'"
            ></i>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">
            {{ creditsMode === 'remove' ? 'Remove Credits' : 'Give Credits' }}
          </h3>
          <p class="text-sm text-gray-600 mb-6">
            Are you sure you want to {{ creditsMode === 'remove' ? 'remove' : 'give' }}
            <strong>{{ creditsToGive }} credits</strong>
            {{ creditsMode === 'remove' ? 'from' : 'to' }}
            <strong>{{ selectedChat?.organizationId }}</strong>?
          </p>
          <div class="flex gap-3 justify-center">
            <button
              (click)="cancelGiveCredits()"
              class="flex-1 sm:flex-none px-4 py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >Cancel</button>
            <button
              (click)="confirmGiveCredits()"
              [disabled]="isGivingCredits"
              class="flex-1 sm:flex-none px-4 py-2.5 text-sm text-white rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              [ngClass]="creditsMode === 'remove' ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'"
            >
              <ion-spinner *ngIf="isGivingCredits" name="crescent" class="w-4 h-4"></ion-spinner>
              <span>{{ isGivingCredits ? (creditsMode === 'remove' ? 'Removing...' : 'Giving...') : 'Confirm' }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ─────────────────────────── Create New Chat Modal ─────────────────────────── -->
    <div *ngIf="showCreateChatModal" class="modal-backdrop" (click)="closeCreateChatModal()">
      <div class="modal-sheet max-w-lg" (click)="$event.stopPropagation()">
        <div class="p-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h2 class="text-lg font-semibold text-gray-900">Create New Chat</h2>
          <button (click)="closeCreateChatModal()" class="tap-target text-gray-400 hover:text-gray-600" aria-label="Close">
            <i class="fa-solid fa-times text-lg"></i>
          </button>
        </div>

        <div class="p-4 flex-1 overflow-y-auto min-h-0">
          <!-- Search Organizations -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Search Organization</label>
            <div class="relative">
              <input
                type="text"
                [(ngModel)]="organizationSearchQuery"
                (keyup.enter)="onOrganizationSearch()"
                placeholder="Search by organization ID..."
                class="chat-input w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
              <i class="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
            </div>
          </div>

          <!-- Organizations List -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Select Organization (without existing chat)</label>
            <div class="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
              <div *ngIf="isLoadingOrganizations" class="flex items-center justify-center py-4">
                <ion-spinner name="crescent" class="text-primary"></ion-spinner>
              </div>

              <div *ngIf="!isLoadingOrganizations && organizationsWithoutChats.length === 0" class="py-4 text-center text-gray-500 text-sm">
                No organizations without chats found
              </div>

              <div
                *ngFor="let org of organizationsWithoutChats"
                (click)="selectOrganization(org.organizationId)"
                class="p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                [class.bg-primary-50]="selectedOrganizationId === org.organizationId"
                [class.border-l-4]="selectedOrganizationId === org.organizationId"
                [class.border-l-primary]="selectedOrganizationId === org.organizationId"
              >
                <div class="flex items-center justify-between gap-2">
                  <span class="font-medium text-sm text-gray-900 truncate">{{ org.organizationId }}</span>
                  <span
                    *ngIf="org.plan"
                    class="px-1.5 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide shrink-0"
                    [class.bg-gray-200]="org.plan === 'free'"
                    [class.text-gray-500]="org.plan === 'free'"
                    [class.bg-blue-600]="org.plan === 'starter' || org.plan === 'shared'"
                    [class.text-white]="org.plan !== 'free'"
                    [class.bg-purple-600]="org.plan === 'profesional'"
                    [class.bg-amber-500]="org.plan === 'business'"
                    [class.bg-gradient-to-r]="org.plan === 'enterprise'"
                    [class.from-indigo-600]="org.plan === 'enterprise'"
                    [class.to-purple-600]="org.plan === 'enterprise'"
                  >{{ org.plan }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Initial Message (optional) -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Initial Message (optional)</label>
            <textarea
              [(ngModel)]="newChatInitialMessage"
              placeholder="Type your first message to start the conversation..."
              class="chat-input w-full px-4 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              rows="3"
            ></textarea>
          </div>
        </div>

        <div class="p-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
          <button
            (click)="closeCreateChatModal()"
            class="flex-1 sm:flex-none px-4 py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            (click)="createNewChat()"
            [disabled]="!selectedOrganizationId || isCreatingChat"
            class="flex-1 sm:flex-none px-4 py-2.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            <ion-spinner *ngIf="isCreatingChat" name="crescent" class="w-4 h-4"></ion-spinner>
            <span>{{ isCreatingChat ? 'Creating...' : 'Create Chat' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ─────────────────────────── Polish Draft Modal ───────────────────────────
         ⚠️ THE CORRECTION LIVES ONLY HERE UNTIL "APPLY" IS PRESSED. Closing this
         modal — backdrop, X or Close — discards it and leaves the composer exactly
         as the admin typed it. Nothing on the server was written either. -->
    <div *ngIf="showPolishModal" class="modal-backdrop" (click)="closePolishModal()">
      <div class="modal-sheet max-w-2xl" (click)="$event.stopPropagation()">
        <div class="p-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h2 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <i class="fa-solid fa-wand-magic-sparkles text-indigo-600 text-base"></i>
            Polished reply
          </h2>
          <button (click)="closePolishModal()" class="tap-target text-gray-400 hover:text-gray-600" aria-label="Close">
            <i class="fa-solid fa-times text-lg"></i>
          </button>
        </div>

        <div class="p-4 flex-1 overflow-y-auto min-h-0">
          <div *ngIf="isPolishing" class="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
            <ion-spinner name="crescent" class="text-indigo-600"></ion-spinner>
            <span>Correcting the grammar…</span>
          </div>

          <div *ngIf="!isPolishing && polishError" class="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {{ polishError }}
          </div>

          <ng-container *ngIf="!isPolishing && !polishError && polishedText">
            <p *ngIf="polishUnchanged" class="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-700">
              Nothing to correct — your draft is already fine.
            </p>

            <label class="block text-xs font-medium text-gray-500 mb-1">Your draft</label>
            <div class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{{ polishSource }}</div>

            <label class="block text-xs font-medium text-gray-500 mb-1">
              Corrected
              <span *ngIf="polishLanguage" class="ml-1 uppercase tracking-wide text-[10px] text-gray-400">{{ polishLanguage }}</span>
            </label>
            <div class="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 text-sm text-gray-900 whitespace-pre-wrap break-words">{{ polishedText }}</div>
          </ng-container>
        </div>

        <div class="p-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
          <button
            (click)="closePolishModal()"
            class="flex-1 sm:flex-none px-4 py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            (click)="copyPolish()"
            [disabled]="!polishedText || isPolishing"
            class="flex-1 sm:flex-none px-4 py-2.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            <i class="fa-solid" [ngClass]="polishCopied ? 'fa-check text-emerald-600' : 'fa-copy'"></i>
            <span>{{ polishCopied ? 'Copied' : 'Copy' }}</span>
          </button>
          <button
            (click)="applyPolish()"
            [disabled]="!polishedText || isPolishing"
            class="flex-1 sm:flex-none px-4 py-2.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- ⭐ The customer panel, shared with the CEO users page (CeoUserDetailModule).
       `detailUserId` is the whole contract: an id opens it, null closes it. -->
  <app-ceo-user-detail
    [userId]="detailUserId"
    (closed)="closeUserDetail()"
    (creditsChanged)="onDetailCreditsChanged()"
  ></app-ceo-user-detail>
</ion-content>
